import { sanitizeNullStrings } from "@/utils/sanitize";
import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Divider,
	FormControlLabel,
	Grid2,
	IconButton,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type { Cartridge } from "../models/Cartridge";
import { MillSizeSelector } from "./MillSizeSelector";

interface FormErrors {
	Name?: string;
	MillSizeId?: string;
}

interface CartridgeDetailViewerProps {
	cartridge: Cartridge | null;
	existingCartridges: Cartridge[];
	isSaving: boolean;
	onSave: (cartridge: Cartridge) => void;
	onClose: () => void;
}

export const CartridgeDetailViewer = ({
	cartridge,
	existingCartridges,
	isSaving,
	onSave,
	onClose,
}: CartridgeDetailViewerProps) => {
	const [formData, setFormData] = useState<Cartridge | null>(null);
	const [errors, setErrors] = useState<FormErrors>({});
	const [touched, setTouched] = useState<Record<string, boolean>>({});

	const isNewCartridge = !cartridge?.Id;

	// Validate form and return errors
	const validateForm = useMemo(() => {
		return (data: Cartridge | null): FormErrors => {
			const newErrors: FormErrors = {};

			if (!data?.Name || data.Name.trim().length === 0) {
				newErrors.Name = "Name is required";
			} else {
				// Check for duplicate name within same mill size (case-insensitive)
				const isDuplicate = existingCartridges.some(
					(c) =>
						c.Id !== data.Id &&
						c.Name.toLowerCase() === data.Name.trim().toLowerCase() &&
						c.MillSizeId === data.MillSizeId,
				);
				if (isDuplicate) {
					newErrors.Name = "This name already exists for this mill size";
				}
			}

			if (data?.MillSizeId == null) {
				newErrors.MillSizeId = "Mill Size is required";
			}

			return newErrors;
		};
	}, [existingCartridges]);

	// Check if form is valid
	const formErrors = useMemo(
		() => validateForm(formData),
		[formData, validateForm],
	);
	const isFormValid = Object.keys(formErrors).length === 0;

	// Check if new cartridge can be created
	const canCreateCartridge = isNewCartridge && isFormValid;

	useEffect(() => {
		if (cartridge) {
			setFormData(sanitizeNullStrings({ ...cartridge }));
		} else {
			setFormData(null);
		}
		// Reset validation state when cartridge changes
		setErrors({});
		setTouched({});
	}, [cartridge]);

	if (!cartridge) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
					color: "text.secondary",
				}}
			>
				<Typography>Select a cartridge to view</Typography>
			</Box>
		);
	}

	if (!formData) return null;

	// Handle field change (updates local state)
	const handleChange = (field: keyof Cartridge, value: unknown) => {
		setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
	};

	// Mark field as touched and show error if any
	const handleBlur = (field: keyof Cartridge) => {
		setTouched((prev) => ({ ...prev, [field]: true }));
		// Update errors for touched field
		if (formData) {
			setErrors(validateForm(formData));
		}
	};

	// Auto-save for existing cartridges (called on blur for text fields)
	const handleBlurSave = (field: keyof Cartridge, value: unknown) => {
		handleBlur(field);

		if (isNewCartridge) return; // Don't auto-save new cartridges

		// Check for validation errors on the field
		const currentErrors = validateForm(
			formData ? { ...formData, [field]: value } : null,
		);
		if (currentErrors[field as keyof FormErrors]) {
			return; // Don't save if there are validation errors
		}

		// Check if value actually changed
		const originalValue = cartridge[field];
		const normalizedOriginal = originalValue === null ? "" : originalValue;
		const normalizedNew = value === null ? "" : value;

		if (normalizedOriginal !== normalizedNew) {
			const updatedCartridge = { ...formData, [field]: value };
			onSave(updatedCartridge);
		}
	};

	// Auto-save for selects (immediate on change)
	const handleSelectChange = (field: keyof Cartridge, value: unknown) => {
		handleChange(field, value);
		setTouched((prev) => ({ ...prev, [field]: true }));

		// Update errors after changing value
		const updatedData = formData ? { ...formData, [field]: value } : null;
		const currentErrors = validateForm(updatedData);
		setErrors(currentErrors);

		if (isNewCartridge) return; // Don't auto-save new cartridges

		// Check for validation errors on the field
		if (currentErrors[field as keyof FormErrors]) {
			return; // Don't save if there are validation errors
		}

		// Check if value actually changed
		if (cartridge[field] !== value) {
			const updatedCartridge = { ...formData, [field]: value };
			onSave(updatedCartridge);
		}
	};

	// Auto-save for checkboxes (immediate on change)
	const handleCheckboxChange = (field: keyof Cartridge, value: unknown) => {
		handleChange(field, value);

		if (isNewCartridge) return; // Don't auto-save new cartridges

		// Check if value actually changed
		if (cartridge[field] !== value) {
			const updatedCartridge = { ...formData, [field]: value };
			onSave(updatedCartridge);
		}
	};

	// Create new cartridge (explicit save)
	const handleCreate = () => {
		// Mark all required fields as touched to show errors
		setTouched({ Name: true, MillSizeId: true });
		setErrors(formErrors);

		if (formData && canCreateCartridge) {
			onSave(formData);
		}
	};

	const sectionHeaderSx = {
		color: "primary.main",
		fontWeight: 600,
		fontSize: "0.85rem",
		mt: 1.5,
		mb: 0.5,
	};

	return (
		<Box sx={{ p: 1.5, height: "100%", overflow: "auto" }}>
			{/* Header */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 1,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
						{isNewCartridge ? "New Cartridge" : formData.Name}
					</Typography>
					{isSaving && <CircularProgress size={16} />}
				</Box>
				<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
					{/* CREATE button only for new cartridges */}
					{isNewCartridge && (
						<Button
							color="primary"
							variant="contained"
							size="small"
							onClick={handleCreate}
							disabled={!canCreateCartridge || isSaving}
						>
							{isSaving ? "CREATING..." : "CREATE"}
						</Button>
					)}
					{/* CLOSE button always visible */}
					<Tooltip title="Close">
						<IconButton size="small" onClick={onClose} disabled={isSaving}>
							<CloseIcon />
						</IconButton>
					</Tooltip>
				</Box>
			</Box>

			<Divider sx={{ mb: 1 }} />

			{/* Form Fields */}
			<Grid2 container spacing={1}>
				<Grid2 size={{ xs: 12 }}>
					<Typography sx={sectionHeaderSx}>Basic Information</Typography>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						label="Name"
						value={formData.Name}
						onChange={(e) => handleChange("Name", e.target.value)}
						onBlur={(e) => handleBlurSave("Name", e.target.value)}
						fullWidth
						size="small"
						required
						error={touched.Name && !!errors.Name}
						helperText={touched.Name && errors.Name}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 6 }}>
					<MillSizeSelector
						value={formData.MillSizeId}
						onChange={(value) => handleSelectChange("MillSizeId", value)}
						fullWidth
						required
						size="small"
						error={touched.MillSizeId && !!errors.MillSizeId}
						helperText={touched.MillSizeId ? errors.MillSizeId : undefined}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<TextField
						label="Description"
						value={formData.Description ?? ""}
						onChange={(e) => handleChange("Description", e.target.value)}
						onBlur={(e) => handleBlurSave("Description", e.target.value)}
						fullWidth
						size="small"
						multiline
						rows={3}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<FormControlLabel
						control={
							<Checkbox
								checked={formData.Active}
								onChange={(e) =>
									handleCheckboxChange("Active", e.target.checked)
								}
								size="small"
							/>
						}
						label="Active"
						sx={{ ml: 0 }}
					/>
				</Grid2>
			</Grid2>
		</Box>
	);
};
